import { shift as dateShift } from "../../../core/helpers/date-util.js";
import AbstractPeriodTemplate from "./AbstractPeriodTemplate.js";

/**
 * Relative period template.
 *
 * @param {string} [spec.shiftPeriodType] - Type of the period to shift by
 * @param {int} [spec.shift] - Number of periods to shift
 */
export default class RelativePeriodTemplate extends AbstractPeriodTemplate {
	constructor({ shiftPeriodType, shift = 0 } = {}) {
		super(...arguments);

		this._shift = shift;
		this._shiftPeriodType = shiftPeriodType || "Current";
	}

	accept(visitor) {
		return visitor.visitRelativePeriodTemplate(this);
	}

	getShift() {
		return this._shift;
	}

	setShift(shift) {
		this._shift = shift;
	}

	getShiftPeriodType() {
		return this._shiftPeriodType;
	}

	setShiftPeriodType(shiftPeriodType) {
		this._shiftPeriodType = shiftPeriodType;
	}

	increaseShift() {
		this._shift++;
	}

	decreaseShift() {
		this._shift--;
	}

	/**
	 * @return {period} - the period to be used in the interpretation.
	 *
	 * The period is computed by shifting the context's period `shift` times
	 * by `shiftPeriodType`, and making a period of type `periodType` from
	 * the result.
	 * @example
	 * let t = new RelativePeriodTemplate({
	 *   periodType: 'Month',
	 *   shift: 2,
	 *   shiftPeriodType: 'Year'
	 * });
	 *
	 * let period = t.getPeriodForContext({period: march2007, someCompany});
	 * // => March 2005
	 *
	 * If no period can be found in the interpretation context's fiscal
	 * year, a custom period is made. In this case, no data will be
	 * requested when requesting report value calculations.
	 */
	getPeriodForContext(context) {
		const period = context.getPeriod();

		const shiftPeriodType = this._getPeriodTypeForContext({
			period,
			type: this._shiftPeriodType,
		});
		const periodType = this._getPeriodTypeForContext({
			period,
			type: this._periodType,
		});

		const resultDate = dateShift({
			date: period.getStart(),
			amount: this._shift,
			periodType: shiftPeriodType,
		});

		return this._makePeriod({
			date: resultDate,
			context,
			periodType,
		});
	}

	_getPeriodTypeForContext({ period, type }) {
		if (type === "Current") {
			return period.getPeriodType();
		}
		return type;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.shift = this._shift;
		properties.shiftPeriodType = this._shiftPeriodType;

		return properties;
	}

	static getType() {
		return "RelativePeriodTemplate";
	}
}
